// The rail down the left: the source's navigation, as one column of icons.
//
// The source reaches its screens two ways, and the rail carries both. Its
// Primary Navigation is six buttons along the bottom; its Menu navigation,
// behind the hamburger, is Archive, Charts and Two Week Production. Here they
// are one list, the six first and the three after, in the source's order.
// Everything else -- the reference views and the tables -- stays in the header.
//
// About holds its place at the foot of the rail, below a divider: it is about
// the application rather than one of its screens.
//
// Admin sits above About, and only for an Admin or an Access Manager, as
// `/api/me` says. Hiding it from everyone else is a courtesy rather than the
// control -- the route turns them away too, and the API refuses every write the
// page makes that the caller may not.
//
// OPENING THE RAIL PUSHES THE SCREEN OVER RATHER THAN COVERING IT. The rail is
// a column in the same row as the screen, so when the names appear the screen
// narrows to make room. Several grids are already wider than the window, and a
// rail laid over them would hide the first columns, which are the ones that
// say which row is which.

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Calendar,
  CalendarDays,
  ChartColumn,
  ClipboardCheck,
  Factory,
  Flame,
  House,
  Info,
  List,
  Menu,
  ShieldCheck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

type Item = { label: string; icon: LucideIcon; to: string };

// Lucide draws outlines only, so the two calendars are told apart by their
// drawing rather than the source's filled and outline pair: the daily tracker
// has the days marked on it.
const ITEMS: (Item | 'divider')[] = [
  { to: '/dashboard', label: 'Dashboard', icon: House },
  { to: '/daily-production-tracker', label: 'Daily Production Tracker', icon: CalendarDays },
  { to: '/barriers/dashboard', label: 'Barriers Dashboard', icon: List },
  { to: '/barriers/hot', label: 'HOT', icon: Flame },
  { to: '/barriers/plants', label: 'Plants', icon: Factory },
  { to: '/barriers/department', label: 'Department', icon: Wrench },
  { to: '/barriers/archive', label: 'Archive', icon: ClipboardCheck },
  { to: '/charts', label: 'Charts', icon: ChartColumn },
  { to: '/two-week-production', label: 'Two Week Production', icon: Calendar },
  'divider',
  { to: '/about', label: 'About', icon: Info },
];

const ADMIN: Item = { to: '/admin', label: 'Admin', icon: ShieldCheck };

function itemsFor(admin: boolean): (Item | 'divider')[] {
  if (!admin) return ITEMS;
  const about = ITEMS.findIndex((item) => item !== 'divider' && item.label === 'About');
  return [...ITEMS.slice(0, about), ADMIN, ...ITEMS.slice(about)];
}

export const RAIL_KEY = 'barriertracker.rail.open';

// Whether the rail was left open, from the last visit. Storage can be missing
// or refuse outright -- a private window, a locked-down profile -- and either
// way the rail starts closed and still opens; it just will not remember.
function readOpen(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(RAIL_KEY, String(open));
  } catch {
    // Not remembered, and nothing else depends on it.
  }
}

export function NavRail({ admin = false }: { admin?: boolean }) {
  const [open, setOpen] = useState(readOpen);

  const toggle = () => {
    setOpen(!open);
    writeOpen(!open);
  };

  return (
    <nav className={open ? 'app-rail open' : 'app-rail'} aria-label="Main">
      <div className="rail-list">
        <button
          type="button"
          className="rail-toggle"
          aria-label="Menu"
          aria-expanded={open}
          title={open ? undefined : 'Menu'}
          onClick={toggle}
        >
          <span className="rail-icon">
            <Menu />
          </span>
        </button>
        <ul>
          {itemsFor(admin).map((item, index) =>
            item === 'divider' ? (
              <li key={index} className="rail-divider" aria-hidden="true" />
            ) : (
              <li key={item.label} title={open ? undefined : item.label}>
                <NavLink to={item.to} aria-label={item.label} className={({ isActive }) => (isActive ? 'active' : undefined)}>
                  <Entry item={item} />
                </NavLink>
              </li>
            )
          )}
        </ul>
      </div>
    </nav>
  );
}

function Entry({ item }: { item: Item }) {
  const Icon = item.icon;
  return (
    <>
      <span className="rail-icon">
        <Icon />
      </span>
      <span className="rail-label">{item.label}</span>
    </>
  );
}
